export type IProgram = {
	id: string;
	_id: string;
	name: string;
};

export type BudgetLimitEnforcementMode = "WARN" | "BLOCK";

export interface BudgetConsumption {
	netEarned: number;
	budgetLimit: number;
	percentage: number;
}

export interface ProgramParameters {
	eligibility: boolean;
	consent: boolean;
	maxBeneficiaries: number;
	durationInWeeks: number;
	maxYearlyEarnings: number;
	budgetLimit: number;
	budgetLimitEnabled?: boolean;
	budgetLimitThreshold?: number;
	budgetLimitEnforcementMode?: BudgetLimitEnforcementMode;
	baseIncentiveNegative: number;
	baseIncentivePositive: number;
	biVisitIncrementNegative: number;
	repetitionPerSteps: number;
	eligibilityConfirmationIntervalDays?: number;
	_id?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	durationConfig?: any;
}

export interface JournalCrisisIntervention {
	enabled?: boolean;
	triggerMoods?: string[];
	title?: string;
	message?: string;
}

export interface CallConfiguration {
	allowCallRequests?: boolean;
	callCooldownMinutes?: number;
	expectedResponseHours?: number;
	// SIMS-9058. Optional, and absent on every program that predates it — an
	// absence must read as "off" rather than as a decision.
	notifyCoordinatorByEmail?: boolean;
}

export interface ProgramFeatures {
	enableJournal?: boolean;
	enableRewards?: boolean;
	enableAutomatedNotifications?: boolean;
	enableActivityAlerts?: boolean;
	enableCallManagement?: boolean;
	// SIMS-4146: Daily Workflow feature flags for the Activity Sidebar.
	activitySidebar?: boolean;
	realtimeAlerts?: boolean;
	allowEnrollmentPause?: boolean;
	allowIncentiveInvalidation?: boolean;
	eventBasedRewards?: boolean;
	journalMultiPage?: boolean;
	journalCrisisIntervention?: JournalCrisisIntervention;
	callConfiguration?: CallConfiguration;
}

// SIMS-4146: per-program Activity Sidebar alert thresholds.
export interface ProgramActivityAlerts {
	delayedThresholdHours?: number;
	enableDelayedAlerts?: boolean;
	enableMissedAlerts?: boolean;
}

export interface ProgramVisibleTabs {
	showWeeklyCalendar?: boolean;
	showDailyJourney?: boolean;
	showClinicalInformation?: boolean;
}

export interface ProgramReportConfig {
	incentiveDisbursement?: boolean;
	educationalEngagement?: boolean;
	eventActivity?: boolean;
	eventRatings?: boolean;
}

export type LocalizedString = Record<string, string> | { def: string };

export enum EducationalContentType {
	VIDEO = "video",
	DOCUMENT = "document",
	LINK = "link",
}

export interface EducationalContentItem {
	id: string;
	type: EducationalContentType;
	title: LocalizedString;
	description: LocalizedString;
	category: string;
	thumbnailUrl: string;
	contentUrl: string;
	duration?: string;
	pageCount?: number;
	createdAt: string;
}

export interface Program {
	_id: string;
	id: string;
	accountId: string;
	name: string;
	type: "Stim" | "OUD";
	status: "ACTIVE" | "INACTIVE";
	multiTreatmentPlan: boolean;
	treatmentPlanChangeAllowed: boolean;
	treatmentPlan?: string;
	orgUnitId?: string[];
	parameters: ProgramParameters;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
	budgetConsumption?: BudgetConsumption;
	cmRoles?: string[];
	additionalStaffRoles?: string[];
	payers?: string[];
	requirePayerPlanReviewOnReconfirm?: boolean;
	incentiveOnDemand?: boolean;
	onDemandIncentiveMin?: number;
	onDemandIncentiveMax?: number;
	features?: ProgramFeatures;
	activityAlerts?: ProgramActivityAlerts;
	visibleTabs?: ProgramVisibleTabs;
	reportConfig?: ProgramReportConfig;
	educationalContent?: EducationalContentItem[];
	newContentDays?: number;
	// SIMS-7013: program incentive configuration (incentiveType/flow mode/auto-banking).
	sims?: ProgramSims;
	// SIMS-7013 (AC9): derived read-only signal — whether the program has >=1 enrollment
	// (drives the post-enrollment field lock). Not persisted; attached by the read API.
	hasEnrollments?: boolean;
}

export interface ProgramRequest {
	accountId: string;
	name: string;
	type: "Stim" | "OUD";
	status: "ACTIVE" | "INACTIVE";
	multiTreatmentPlan: boolean;
	treatmentPlanChangeAllowed: boolean;
	treatmentPlan?: string;
	orgUnitId?: string[];
	parameters: ProgramParameters;
	createdBy: string;
	cmRoles?: string[];
	additionalStaffRoles?: string[];
	payers?: string[];
	requirePayerPlanReviewOnReconfirm?: boolean;
	incentiveOnDemand?: boolean;
	onDemandIncentiveMin?: number;
	onDemandIncentiveMax?: number;
	features?: ProgramFeatures;
	activityAlerts?: ProgramActivityAlerts;
	visibleTabs?: ProgramVisibleTabs;
	reportConfig?: ProgramReportConfig;
	educationalContent?: EducationalContentItem[];
	newContentDays?: number;
	// SIMS-7013: program incentive configuration. Sent whole on create/update because
	// the backend PUT replaces the `sims` sub-document (preserve non-managed fields).
	sims?: ProgramSims;
}

export type Params = {
	accountId: string;
	userId?: string;
};

export enum IncentiveTypes {
	GC = "GC",
	PPC = "PPC",
	EXTERNAL = "EXTERNAL",
}

// SIMS-7011/7013: how a program's earned incentive may be disbursed at visit entry.
// BOTH = bank or cash-out; FORCE_CASHOUT = cash-out only; BANK_ONLY = bank only.
export enum IncentiveFlowMode {
	BOTH = "BOTH",
	FORCE_CASHOUT = "FORCE_CASHOUT",
	BANK_ONLY = "BANK_ONLY",
}

// SIMS-7013: the program `sims` incentive-configuration sub-object. Mirrors the
// backend SimsDto (cmi-backend-lambdas-sims-application-02 CreateProgram.dto.ts).
// The three managed fields (incentiveType/incentiveFlowMode/autoBanking) are edited
// by the program-configuration form (SIMS-7070); the remaining fields are preserved
// verbatim on save because the backend PUT replaces the whole `sims` sub-document.
export interface ProgramSims {
	incentiveType: IncentiveTypes;
	incentiveFlowMode?: IncentiveFlowMode;
	autoBanking?: boolean;
	// Preserved-on-save (owned by other flows; not edited here):
	udtVisits?: unknown[];
	allowPartialRedeems?: boolean;
	minRedemptionAmount?: number;
}
